import { useInfiniteQuery } from '@tanstack/react-query'
import { memo, useMemo } from 'react'

import { formatDecimalPlaces } from '../../../helpers/functions/format-decimal-places'
import {
  gameGoldMinerService,
  type GoldMinerHistoryRow,
} from '../../../services/games/games-golds-miners'
import closeSvg from '../modal-games/components/images/close.svg'

interface Props {
  onClose: () => void
}

const PAGE = 40

const dayKey = (d: Date) =>
  `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`

function dayLabel(d: Date) {
  const today = new Date()
  const yesterday = new Date()
  yesterday.setDate(today.getDate() - 1)
  if (dayKey(d) === dayKey(today)) return 'Hoje'
  if (dayKey(d) === dayKey(yesterday)) return 'Ontem'
  return d.toLocaleDateString('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

const timeLabel = (d: Date) =>
  d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })

/**
 * "Meus ganhos" — o que o jogador minerou, mais recente primeiro, quebrado por
 * dia (no fuso do aparelho). Cada linha = um presente de uma jogada: imagem,
 * nome, ×quantidade, valor UNITÁRIO e horário. Carrega de 40 em 40 ao rolar.
 */
export const ModalHistory = memo(({ onClose }: Props) => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      queryKey: ['gold-miner-history'],
      queryFn: ({ pageParam }) => gameGoldMinerService.getHistory(pageParam, PAGE),
      initialPageParam: 0,
      getNextPageParam: (last, all) => (last.length < PAGE ? undefined : all.length),
      staleTime: 0,
    })

  const days = useMemo(() => {
    const groups: { key: string; label: string; rows: GoldMinerHistoryRow[] }[] = []
    for (const row of data?.pages.flat() ?? []) {
      const d = new Date(row.createdAt)
      const key = dayKey(d)
      let g = groups[groups.length - 1]
      if (!g || g.key !== key) {
        g = { key, label: dayLabel(d), rows: [] }
        groups.push(g)
      }
      g.rows.push(row)
    }
    return groups
  }, [data])

  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    if (
      hasNextPage &&
      !isFetchingNextPage &&
      el.scrollTop + el.clientHeight >= el.scrollHeight - 120
    ) {
      fetchNextPage()
    }
  }

  return (
    <div className="absolute inset-0 z-30 flex flex-col overflow-hidden rounded-t-2xl bg-gradient-to-b from-[#191C21] to-black">
      <div className="relative flex shrink-0 items-center justify-center px-4 pt-4 pb-3">
        <h2 className="text-lg font-extrabold tracking-wide text-white drop-shadow">
          Meus ganhos
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="absolute top-1/2 right-3 -translate-y-1/2"
        >
          <img src={closeSvg} alt="Fechar" className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 pb-6" onScroll={onScroll}>
        {isLoading ? (
          <p className="mt-10 text-center text-sm text-white/50">Carregando…</p>
        ) : days.length === 0 ? (
          <p className="mt-10 text-center text-sm text-white/50">
            Você ainda não minerou nada. Boa sorte!
          </p>
        ) : (
          days.map((day) => (
            <section key={day.key} className="mb-3">
              {/* cabeçalho do dia gruda no topo enquanto rola */}
              <div className="sticky top-0 z-10 -mx-3 bg-[#191C21]/95 px-3 py-1.5 backdrop-blur-sm">
                <span className="text-xs font-extrabold tracking-wide text-[#AFC0F5]/90 uppercase">
                  {day.label}
                </span>
              </div>
              <ul className="mt-1 flex flex-col gap-1.5">
                {day.rows.map((row, i) => {
                  const d = new Date(row.createdAt)
                  return (
                    <li
                      key={`${row.createdAt}-${row.giftIdEarning}-${i}`}
                      className="flex items-center gap-3 rounded-xl border border-[#4261CB]/25 bg-white/5 px-2.5 py-1.5"
                    >
                      <div className="relative h-10 w-10 shrink-0">
                        {row.imgGift ? (
                          <img
                            src={row.imgGift}
                            alt={row.giftName ?? ''}
                            className="h-10 w-10 object-contain"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-lg bg-[#4261CB]/20" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-bold text-white/90">
                          {row.giftName ?? `Presente #${row.giftIdEarning}`}
                        </p>
                        <p className="text-[11px] text-white/45 tabular-nums">
                          {timeLabel(d)}
                        </p>
                      </div>
                      {row.quantity > 1 && (
                        <span className="rounded bg-[#4261CB] px-1.5 text-[11px] font-black text-white">
                          ×{row.quantity}
                        </span>
                      )}
                      {/* valor UNITÁRIO do presente — nunca valor×quantidade */}
                      <span className="w-14 text-right text-sm font-extrabold text-amber-300 tabular-nums">
                        {formatDecimalPlaces(row.valueGift)}
                      </span>
                    </li>
                  )
                })}
              </ul>
            </section>
          ))
        )}
        {isFetchingNextPage && (
          <p className="py-3 text-center text-xs text-white/40">Carregando…</p>
        )}
      </div>
    </div>
  )
})

ModalHistory.displayName = 'ModalHistory'
